import { ApiClientWrapper } from '@spike/api-client';
import { convertMarketplace, MarketplaceAction, setMarketplace } from '@spike/marketplace-action';
import { NotificationsAction, showErrorThunk, showSuccessThunk } from '@spike/notifications-action';
import {
    WAITLISTS_CLOSE_PANEL_ACTION_TYPE,
    WAITLISTS_COUNTS_FETCH_SUCCESS_ACTION_TYPE,
    WAITLISTS_DELETE_START_ACTION_TYPE,
    WAITLISTS_DELETE_SUCCESS_ACTION_TYPE,
    WAITLISTS_ERROR_ACTION_TYPE,
    WAITLISTS_FETCH_START_ACTION_TYPE,
    WAITLISTS_FETCH_SUCCESS_ACTION_TYPE,
    WAITLISTS_GET_START_ACTION_TYPE,
    WAITLISTS_GET_SUCCESS_ACTION_TYPE,
    WAITLISTS_OPEN_PANEL_ACTION_TYPE,
    WAITLISTS_RESET_ACTION_TYPE,
    WAITLISTS_HIDE_FORM_ACTION_TYPE,
    WAITLISTS_SAVE_SUCCESS_ACTION_TYPE,
    WAITLISTS_SELECT_ACTION_TYPE,
    WAITLISTS_SHOW_FORM_ACTION_TYPE,
    WAITLISTS_START_CONVERSION_ACTION_TYPE,
    WAITLISTS_END_CONVERSION_ACTION_TYPE,
    WAITLISTS_REFRESH_LIST_ACTION_TYPE,
    WaitlistsAction
} from 'actions/waitlists/WaitlistsActionsTypes';
import { convertToWaitlist, convertToWaitlistCounts } from 'actions/waitlists/WaitlistsConverter';
import { FetchWaitlistCountsResponseDto, FetchWaitlistsResponseDto, WaitlistDto } from 'actions/waitlists/WaitlistsDto';
import ApiClient from 'api';
import { createTokenConfig } from 'api/ApiClient';
import { AxiosError } from 'axios';
import { formatWaitlistErrors, WaitlistInputDto } from 'components/Waitlist/WaitlistForm/waitlistFormUtils';
import { AppointmentMultiple } from 'model/NewMultipleBooking';
import { Waitlist, WaitlistKey, WaitlistsFilter } from 'model/Waitlist';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';

const resourceUrl = '/waitlists';
const marketplacesUrl = '/marketplaces';

export const OLB_WAITLIST_ENABLED_KEY = 'olb_waitlist_enabled';
const OLB_WAITLIST_ENABLED_NAME = 'Allow Clients to Join Waitlist';
const countsUrl = `${resourceUrl}/counts`;

export const WAITLISTS_PAGE_SIZE = 20;

const toMarketplaceIdsParam = (marketplaceIds: Array<number>) =>
    marketplaceIds.length > 0 ? marketplaceIds.join(',') : undefined;

export const reset = (): WaitlistsAction => ({ type: WAITLISTS_RESET_ACTION_TYPE });

export const openWaitlistPanel = (
    key: WaitlistKey,
    marketplaceIds: Array<number>,
    waitlistId?: number
): WaitlistsAction => ({
    type: WAITLISTS_OPEN_PANEL_ACTION_TYPE,
    payload: { key, marketplaceIds, waitlistId }
});

export const closeWaitlistPanel = (): WaitlistsAction => ({ type: WAITLISTS_CLOSE_PANEL_ACTION_TYPE });

export const selectWaitlist = (waitlistId?: number): WaitlistsAction => ({
    type: WAITLISTS_SELECT_ACTION_TYPE,
    payload: { waitlistId }
});

export const showWaitlistForm = (waitlistId?: number): WaitlistsAction => ({
    type: WAITLISTS_SHOW_FORM_ACTION_TYPE,
    payload: { waitlistId }
});

export const startWaitlistConversion = (
    waitlist: Waitlist,
    appointments: Array<AppointmentMultiple>
): WaitlistsAction => ({
    type: WAITLISTS_START_CONVERSION_ACTION_TYPE,
    payload: { waitlist, appointments }
});

export const endWaitlistConversion = (booked: boolean): WaitlistsAction => ({
    type: WAITLISTS_END_CONVERSION_ACTION_TYPE,
    payload: { booked }
});

// The open list fetches again and the Waitlist (n) button gets fresh counts.
export const refreshWaitlistsThunk = (
    marketplaceIds: Array<number>
): ThunkAction<void, RootState, null, WaitlistsAction | NotificationsAction> => {
    return dispatch => {
        dispatch({ type: WAITLISTS_REFRESH_LIST_ACTION_TYPE });
        dispatch(fetchWaitlistCountsThunk(marketplaceIds));
    };
};

export const hideWaitlistForm = (): WaitlistsAction => ({ type: WAITLISTS_HIDE_FORM_ACTION_TYPE });

export interface SaveWaitlistResult {
    waitlist?: Waitlist;
    errors: Array<string>;
}

// Create (no waitlistId) or partial update. Validation errors are returned to the form instead of a toast.
export const saveWaitlistThunk = (
    marketplaceId: number,
    input: WaitlistInputDto,
    waitlistId?: number
): ThunkAction<Promise<SaveWaitlistResult>, RootState, null, WaitlistsAction | NotificationsAction> => {
    return async (dispatch, getState) => {
        const {
            login,
            marketplace: { marketplace }
        } = getState();

        const body = { marketplace_id: marketplaceId, waitlist: input };
        const config = createTokenConfig(String(login.auth.token));

        try {
            const { data } = waitlistId
                ? await ApiClient.patch<WaitlistDto>(`${resourceUrl}/${waitlistId}`, body, config)
                : await ApiClient.post<WaitlistDto>(resourceUrl, body, config);

            const waitlist = convertToWaitlist(data, marketplace?.schedule.timeZone);

            dispatch({ type: WAITLISTS_SAVE_SUCCESS_ACTION_TYPE, payload: { waitlist } });
            dispatch(showSuccessThunk(waitlistId ? 'Waitlist request updated.' : 'Client added to the waitlist.'));

            return { waitlist, errors: [] };
        } catch (error) {
            const response = (error as AxiosError)?.response;
            const data = response?.data as { error?: string } | Record<string, Array<string>> | undefined;

            if (response?.status === 422 && data) {
                const message = (data as { error?: string }).error;
                return {
                    errors: message ? [message] : formatWaitlistErrors(data as Record<string, Array<string>>)
                };
            }

            return { errors: ['The waitlist request could not be saved. Please try again.'] };
        }
    };
};

export const fetchWaitlistsThunk = (
    filter: WaitlistsFilter,
    page = 1,
    size = WAITLISTS_PAGE_SIZE
): ThunkAction<void, RootState, null, WaitlistsAction | NotificationsAction> => {
    return async (dispatch, getState) => {
        dispatch({ type: WAITLISTS_FETCH_START_ACTION_TYPE });

        const {
            login,
            marketplace: { marketplace }
        } = getState();

        try {
            const { data: response } = await ApiClient.get<FetchWaitlistsResponseDto>(resourceUrl, {
                ...createTokenConfig(String(login.auth.token)),
                params: {
                    page,
                    size,
                    view: filter.view,
                    key: filter.key,
                    marketplace_id: marketplace?.id,
                    marketplace_ids: toMarketplaceIdsParam(filter.marketplaceIds),
                    ...(filter.search.trim().length > 0 && { q: filter.search.trim() })
                }
            });

            dispatch({
                type: WAITLISTS_FETCH_SUCCESS_ACTION_TYPE,
                payload: {
                    waitlists: response.waitlists.map(dto => convertToWaitlist(dto, marketplace?.schedule.timeZone)),
                    pagination: {
                        page: response.page,
                        size: response.size,
                        totalPages: response.total_pages,
                        totalRecords: response.total_records
                    },
                    append: page > 1
                }
            });
        } catch (error) {
            dispatch({ type: WAITLISTS_ERROR_ACTION_TYPE });
        }
    };
};

export const fetchWaitlistCountsThunk = (
    marketplaceIds: Array<number>
): ThunkAction<void, RootState, null, WaitlistsAction | NotificationsAction> => {
    return async (dispatch, getState) => {
        const {
            login,
            marketplace: { marketplace }
        } = getState();

        try {
            const { data: response } = await ApiClient.get<FetchWaitlistCountsResponseDto>(countsUrl, {
                ...createTokenConfig(String(login.auth.token)),
                params: {
                    marketplace_id: marketplace?.id,
                    marketplace_ids: toMarketplaceIdsParam(marketplaceIds)
                }
            });

            dispatch({
                type: WAITLISTS_COUNTS_FETCH_SUCCESS_ACTION_TYPE,
                payload: { counts: convertToWaitlistCounts(response) }
            });
        } catch (error) {
            // Counts are secondary: keep the last known values instead of breaking the Bookings header.
        }
    };
};

export const getWaitlistThunk = (
    waitlistId: number
): ThunkAction<Promise<boolean>, RootState, null, WaitlistsAction | NotificationsAction> => {
    return async (dispatch, getState) => {
        dispatch({ type: WAITLISTS_GET_START_ACTION_TYPE });

        const {
            login,
            marketplace: { marketplace }
        } = getState();

        try {
            const { data: response } = await ApiClient.get<WaitlistDto>(`${resourceUrl}/${waitlistId}`, {
                ...createTokenConfig(String(login.auth.token)),
                params: { marketplace_id: marketplace?.id }
            });

            dispatch({
                type: WAITLISTS_GET_SUCCESS_ACTION_TYPE,
                payload: { waitlist: convertToWaitlist(response, marketplace?.schedule.timeZone) }
            });

            return true;
        } catch (error) {
            dispatch({ type: WAITLISTS_ERROR_ACTION_TYPE });
            dispatch(showErrorThunk('This waitlist request is no longer available.'));

            return false;
        }
    };
};

export const deleteWaitlistThunk = (
    waitlistId: number
): ThunkAction<Promise<boolean>, RootState, null, WaitlistsAction | NotificationsAction> => {
    return async (dispatch, getState) => {
        dispatch({ type: WAITLISTS_DELETE_START_ACTION_TYPE });

        const {
            login,
            marketplace: { marketplace }
        } = getState();

        try {
            await ApiClient.delete(`${resourceUrl}/${waitlistId}`, {
                ...createTokenConfig(String(login.auth.token)),
                params: { marketplace_id: marketplace?.id }
            });

            dispatch({ type: WAITLISTS_DELETE_SUCCESS_ACTION_TYPE, payload: { waitlistId } });
            dispatch(showSuccessThunk('Waitlist request deleted.'));

            return true;
        } catch (error) {
            dispatch({ type: WAITLISTS_ERROR_ACTION_TYPE });
            dispatch(showErrorThunk('The waitlist request could not be deleted. Please try again.'));

            return false;
        }
    };
};

// Same request the library uses for the other Online Booking toggles (e.g. Card on File):
// PATCH the marketplace with only this setting.
export const saveOlbWaitlistEnabledThunk = (
    apiClientWrapper: ApiClientWrapper,
    enabled: boolean
): ThunkAction<Promise<boolean>, RootState, null, MarketplaceAction | NotificationsAction> => {
    return async (dispatch, getState) => {
        const settings = getState().marketplace.marketplace.settings;
        const existing = settings.find(setting => setting.key === OLB_WAITLIST_ENABLED_KEY);

        try {
            const { data } = await apiClientWrapper.apiClient.patch(
                `${marketplacesUrl}/${apiClientWrapper.marketplaceId}`,
                {
                    marketplace_id: apiClientWrapper.marketplaceId,
                    marketplace: {
                        settings_attributes: [
                            existing
                                ? {
                                      id: existing.id,
                                      key: OLB_WAITLIST_ENABLED_KEY,
                                      name: existing.name,
                                      value: enabled
                                  }
                                : { key: OLB_WAITLIST_ENABLED_KEY, name: OLB_WAITLIST_ENABLED_NAME, value: enabled }
                        ]
                    }
                },
                apiClientWrapper.requestConfig
            );

            dispatch(setMarketplace(convertMarketplace(data, apiClientWrapper.masterData)));
            dispatch(showSuccessThunk('Waitlist setting changed!'));

            return true;
        } catch (error) {
            dispatch(showErrorThunk('Error saving the waitlist setting.'));

            return false;
        }
    };
};
